import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {Component, ChangeDetectionStrategy, Inject, NgZone, ViewChild, ChangeDetectorRef, OnDestroy, AfterViewInit} from '@angular/core';
import {AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { addHours, getHours, startOfDay } from 'date-fns';
import {defaultIfEmpty, delay, first, map, observeOn, shareReplay, startWith, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import { SnackBarService } from '../../../../core-services/snack-bar/snack-bar.service';
import {
  AffiliateExclusiveOption, EditMerchantAdRequest,
  MerchantAd,
  TagExclusiveOption
} from '../../../../core/dtos/api';
import { AdManagementService } from '../../ad-management.service';
import { Validators } from '@angular/forms';
import {asyncScheduler, combineLatest, forkJoin, merge, Observable, of, Subject} from 'rxjs';
import { MerchantApiService } from '../../../../core/services/api/merchant-api.service';
import {DashboardApiService} from '../../../../core/services/api/dashboard-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { utcToMtTime } from '../../../../core/pure-utils/string-utils';
import { merchantUrlValidator } from '../../../../core/pure-utils/validators';
import {TitleCasePipe} from '@angular/common';
import { isDate } from 'date-fns/isDate';
import {isNullOrEmpty} from '../../../../core/pure-utils/object-utils';
import {hourTimeOptions} from '../../../../core/core-variables';

interface RowValue {
  linkText: string;
  adTitle: string;
  destinationUrl: string;
  startDateStartOfDay: Date;
  startDateHour: number;
  endDateStartOfDay: Date;
  endDateHour: number;
  affiliateExclusive: AffiliateExclusiveOption | TagExclusiveOption;
}

@Component({
    templateUrl: './edit-ads-dialog.component.html',
    styleUrls: ['./edit-ads-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EditAdsDialogComponent implements AfterViewInit, OnDestroy {

  constructor(public dialogRef: MatDialogRef<EditAdsDialogComponent>, @Inject(MAT_DIALOG_DATA)
              public data: {merchantAds: MerchantAd[]; userAffiliates: AffiliateExclusiveOption[]; userTagGroups: TagExclusiveOption[]},
              private fb: UntypedFormBuilder, private adManagementService: AdManagementService, private ngZone: NgZone, private snackBarService: SnackBarService,
              private merchantApiService: MerchantApiService, private dashboardApiService: DashboardApiService, private changeDetectorRef: ChangeDetectorRef,
              private titleCasePipe: TitleCasePipe) {}


  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  readonly allAffiliatesOption = <AffiliateExclusiveOption> { affiliate_name: 'All Affiliates', filterType: 'affiliates' };
  readonly exclusiveAffiliateLimit = 100;
  readonly exclusiveTagLimit = 10;
  bulkExclusiveFilterPreviousInput = this.allAffiliatesOption;
  exclusiveFilterPreviousInputs = [];

  ngOnDestroy$ = new Subject();
  ngAfterViewInit$ = new Subject();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly hourTimeOptions = hourTimeOptions;
  saveErrorOccurred = false;
  savingInProgress = false;
  merchantId: string;
  currentAccount$Sub = this.dashboardApiService.currentAccount$.pipe(first()).subscribe(x => this.merchantId = x.entity_id);
  mtNow = utcToMtTime(new Date());
  startDateInFutureExists = this.data.merchantAds.some(x => x.start_date > this.mtNow);
  visibleColumns = this.startDateInFutureExists ? ['adId', 'ad', 'adDetails', 'affiliateDesignation', 'startDate', 'endDate',] :
    ['adId', 'ad', 'adDetails', 'affiliateDesignation', 'endDate'];
  merchantUrls$ = this.adManagementService.merchantWebsites$.pipe(takeUntil(this.ngOnDestroy$), shareReplay(1));

  topControls: {rows: UntypedFormArray} = {
    rows: new UntypedFormArray([])
  };
  topFormGroup: UntypedFormGroup = this.fb.group(this.topControls);

  merchantAds$ = this.ngAfterViewInit$.pipe(
    switchMap(() => {
      if (this.data.merchantAds.length === 1 && this.data.merchantAds[0].restricted_entity_group_name == null) {
        return combineLatest([of([]), of([])]);
      }
      return combineLatest([of(this.data.userAffiliates), of(this.data.userTagGroups)]);
    }),
    delay(0), // Delay as to not cause an expression changed error in dev.
    map(([userAffiliates, userTagGroups]) => <const> [this.data.merchantAds, userAffiliates, userTagGroups]),
    tap(([merchantAds, userAffiliates, userTagGroups]) => {
      this.topControls = {
        rows: this.fb.array(merchantAds.map(singleMerchantAd => this.fb.group(this.createControlRow(singleMerchantAd, userAffiliates, userTagGroups))))
      };
      this.topFormGroup = this.fb.group(this.topControls);
      this.topFormGroup.updateValueAndValidity();
      this.topFormGroup.markAllAsTouched();
      this.changeDetectorRef.detectChanges();
    }),
    map(([merchantAds,,]) => merchantAds),
    startWith([])
  );

  mergedExclusiveInputChanges$ = this.merchantAds$.pipe(
    switchMap(merchantAds => merge(...[...this.topControls.rows.controls.map((x: UntypedFormGroup) =>
      <Observable<string>> x.get('affiliateExclusive').valueChanges),
      <Observable<string>> this.bulkEditControls.affiliateExclusive.valueChanges]))
  );

  controlValidators = {
    linkText: Validators.maxLength(255),
    adId: undefined,
    adTitle: [Validators.required, Validators.maxLength(255)],
    destinationUrl: [Validators.required, Validators.maxLength(255)],
    startDateStartOfDay: Validators.required,
    startDateHour: undefined,
    endDateStartOfDay: undefined,
    endDateHour: undefined,
    affiliateExclusive: undefined
  };

  destinationUrlAsyncValidator = merchantUrlValidator(this.merchantUrls$);

  bulkEditControls = {
    bulkEditSelect: this.fb.control('none'),
    adTitle: this.fb.control('', this.controlValidators.adTitle),
    destinationUrl: this.fb.control('', this.controlValidators.destinationUrl, this.destinationUrlAsyncValidator),
    startDateStartOfDay: this.fb.control(null, this.controlValidators.startDateStartOfDay),
    startDateHour: this.fb.control(0, this.controlValidators.startDateHour),
    endDateStartOfDay: this.fb.control(null, this.controlValidators.endDateStartOfDay),
    endDateHour: this.fb.control(0, this.controlValidators.endDateHour),
    affiliateExclusive: this.fb.control(this.allAffiliatesOption, this.controlValidators.affiliateExclusive),
  };

  createControlRow(merchantAd: MerchantAd, userAffiliates: AffiliateExclusiveOption[], userTagGroups: TagExclusiveOption[]) {
    let selectedExclusiveEntity;
    if (merchantAd.restricted_entity_group_name === 'tag_groups') {
      selectedExclusiveEntity = userTagGroups.find(x => x.tag_id === merchantAd.restricted_entity_id);
      selectedExclusiveEntity = selectedExclusiveEntity ?? {...selectedExclusiveEntity, filterType: 'tag_groups'};
    }
    if(merchantAd.restricted_entity_group_name === 'affiliates') {
      selectedExclusiveEntity = userAffiliates.find(x => x.affiliate_id === merchantAd.restricted_entity_id);
      selectedExclusiveEntity = selectedExclusiveEntity ?? {...selectedExclusiveEntity, filterType: 'affiliates'};
    }
    selectedExclusiveEntity = selectedExclusiveEntity || this.allAffiliatesOption;
    this.exclusiveFilterPreviousInputs.push(selectedExclusiveEntity);

    let controlRow = {
      linkText: this.fb.control(merchantAd.link_text, this.controlValidators.linkText),
      adId: this.fb.control({value: merchantAd.classic_merchant_ad_id, disabled: true}),
      adTitle: this.fb.control(merchantAd.ad_title, this.controlValidators.adTitle),
      destinationUrl: this.fb.control(merchantAd.destination_url, this.controlValidators.destinationUrl, this.destinationUrlAsyncValidator),
      startDateStartOfDay: this.fb.control(merchantAd.start_date == null ? null : startOfDay(merchantAd.start_date), this.controlValidators.startDateStartOfDay),
      startDateHour: this.fb.control(merchantAd.start_date == null ? 0 : getHours(merchantAd.start_date), this.controlValidators.startDateHour),
      endDateStartOfDay: this.fb.control(merchantAd.end_date == null ? null : startOfDay(merchantAd.end_date), this.controlValidators.endDateStartOfDay),
      endDateHour: this.fb.control({
        value: merchantAd.end_date == null ? 0 : getHours(merchantAd.end_date),
        disabled: merchantAd.end_date == null
      }, this.controlValidators.endDateHour),
      affiliateExclusive: this.fb.control(selectedExclusiveEntity, this.controlValidators.affiliateExclusive)
    };

    combineLatest([
      controlRow.endDateStartOfDay.valueChanges.pipe(tap((value) => {
          if (value) {
            controlRow.endDateHour.enable();
          } else {
            controlRow.endDateHour.disable();
            controlRow.endDateHour.setValue(0);
          }
          controlRow.endDateHour.markAsTouched();
        }),
        startWith(controlRow.endDateStartOfDay.value)),
      controlRow.endDateHour.valueChanges.pipe(startWith(controlRow.endDateHour.value)),
    ]).pipe(takeUntil(this.ngOnDestroy$)).subscribe(([endDateStartOfDay, endDateHour]: [Date, number]) => {
      if (!isDate(endDateStartOfDay)) {
        controlRow.endDateHour.setErrors(null);
        return;
      }

      let scheduledStartDate = utcToMtTime(merchantAd.schedule_start_date);
      let endDate = addHours(endDateStartOfDay, endDateHour);

      if (endDate <= scheduledStartDate) {
        controlRow.endDateHour.setErrors({endDateBeforeScheduleDate: true});
      } else {
        let errors = {...controlRow.endDateHour.errors};
        delete errors.endDateBeforeScheduleDate;

        if (isNullOrEmpty(errors)) {
          controlRow.endDateHour.setErrors(null);
        }
      }

      this.changeDetectorRef.detectChanges();
    });

    return controlRow;
  }

  affiliateFilteredOptions$ = combineLatest([this.mergedExclusiveInputChanges$, of(this.data.userAffiliates)]).pipe(
    map(([affiliateFilter, affiliateOptions]) => {
      if (affiliateFilter === '' || typeof affiliateFilter !== 'string') {
        return affiliateOptions;
      }

      let lowerInput = affiliateFilter.toLowerCase();
      return affiliateOptions.filter(x => x.affiliate_name.toLowerCase().indexOf(lowerInput) !== -1 ||
        x.affiliate_website_url_protocol_stripped.indexOf(lowerInput) !== -1 ||
        String(x.classic_affiliate_id).indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));

  affiliateFilteredOptionsLimited$ = this.affiliateFilteredOptions$.pipe(
    map(x => x.length > this.exclusiveAffiliateLimit ? x.slice(0, this.exclusiveAffiliateLimit) : x));

  userTagFilteredOptions$ = combineLatest([this.mergedExclusiveInputChanges$, of(this.data.userTagGroups)]).pipe(
    map(([exclusiveFilter, tagOptions]) => {
      if (exclusiveFilter === '' || typeof exclusiveFilter !== 'string') {
        return tagOptions;
      }

      let lowerInput = exclusiveFilter.toLowerCase();
      return tagOptions.filter(x => x.tag_name.toLowerCase().indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));

  userTagFilteredOptionsLimited$ = this.userTagFilteredOptions$.pipe(
    map(x => x.length > this.exclusiveTagLimit ? x.slice(0, this.exclusiveTagLimit) : x));

  exclusiveFilterDisplayWith = (value: AffiliateExclusiveOption | TagExclusiveOption): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (value.filterType === 'affiliates') {
      return value.affiliate_name.trim();
    }
    if (value.filterType === 'tag_groups') {
      return this.titleCasePipe.transform(`${value.tag_name.trim()} Tag Group`);
    }
    return value;
  };

  autoSelectOnOpened = (formControl: AbstractControl) => formControl.setValue('');

  exclusiveFilterAutoCompleteOnSelected = ($event: MatAutocompleteSelectedEvent, filteredExclusiveInput: HTMLInputElement, controlIndex: number) => {
    this.exclusiveFilterPreviousInputs[controlIndex] = $event.option.value;
    filteredExclusiveInput.blur();
  };

  exclusiveFilterAutoCompleteOnClosed = (formControl: AbstractControl, controlIndex: number) => {
    if (typeof formControl.value === 'string') {
      formControl.setValue(this.exclusiveFilterPreviousInputs[controlIndex]);
    }
  };

  bulkExclusiveFilterAutoCompleteOnSelected = ($event: MatAutocompleteSelectedEvent, filteredExclusiveInput: HTMLInputElement) => {
    this.bulkExclusiveFilterPreviousInput = $event.option.value;
    filteredExclusiveInput.blur();
  };

  bulkExclusiveFilterAutoCompleteOnClosed = (formControl: AbstractControl) => {
    if (typeof formControl.value === 'string') {
      formControl.setValue(this.bulkExclusiveFilterPreviousInput);
    }
  };

  onImgClick(controlRow: number) {
    let fileInputElement = document.getElementById(`fileInput[${controlRow}]`);
    if(fileInputElement) {
      fileInputElement.click();
    }
  }

  onFileInputChange(event, controlRow: number) {
    let fileInputElement: HTMLInputElement = event.currentTarget;
    let file = fileInputElement?.files?.[0];
    let fileImageElement = (<HTMLImageElement> document.getElementById(`fileImage[${controlRow}]`));

    let invalidFileReset = (msg: string) => {
      fileInputElement.value = null;
      fileImageElement.src = this.data.merchantAds[controlRow].image_url;
      this.snackBarService.openErrorSnackBar(msg);
    };

    if (file) {
      let maxFileSize = 300 * 1024; // 300KB
      let lowerFileName = file.name.toLowerCase();

      if (!(lowerFileName.endsWith('.jpg') || lowerFileName.endsWith('.jpeg') || lowerFileName.endsWith('.gif') || lowerFileName.endsWith('.png'))) {
        invalidFileReset('The image must be a .jpg, .jpeg, .gif or .png type.');
        return;
      } else if (file.size > maxFileSize) {
        invalidFileReset('Max size of ad image is 300KB.');
        return;
      }

      let fileImageObject = URL.createObjectURL(file);
      let tempImg = new Image();
      tempImg.src = fileImageObject;

      tempImg.onload = () => {
        let originalWidth = this.data.merchantAds[controlRow].image_width;
        let originalHeight = this.data.merchantAds[controlRow].image_height;

        if (tempImg.naturalWidth !== originalWidth || tempImg.naturalHeight !== originalHeight) {
          window.URL.revokeObjectURL(fileImageObject);
          invalidFileReset('New ad image dimensions must match previous ad image.');
        } else {
          fileImageElement.src = fileImageObject;
        }
      };
    }
  }

  resizeToFitContentSub = this.ngZone.onStable.pipe(take(1)).subscribe(() => this.autosize?.resizeToFitContent(true));

  bulkFormValid() {
    let bulkEditSelectValue = this.bulkEditControls.bulkEditSelect.value;

    return bulkEditSelectValue === 'adTitle' ? this.bulkEditControls.adTitle.valid :
      bulkEditSelectValue === 'destinationUrl' ? this.bulkEditControls.destinationUrl.valid :
      bulkEditSelectValue === 'startDateTime' ? this.bulkEditControls.startDateStartOfDay.valid :
      bulkEditSelectValue === 'endDateTime' ? this.bulkEditControls.endDateStartOfDay.valid :
      bulkEditSelectValue === 'affiliateExclusive' ? this.bulkEditControls.affiliateExclusive.valid : false;
  }

  onBulkApplyClick() {
    let bulkEditSelectValue = this.bulkEditControls.bulkEditSelect.value;
    let rowControls = this.topControls.rows.controls;

    if (bulkEditSelectValue === 'adTitle') {
      rowControls.forEach((fg: UntypedFormGroup) => fg.patchValue({adTitle: this.bulkEditControls.adTitle.value}));
    } else if (bulkEditSelectValue === 'destinationUrl') {
      rowControls.forEach((fg: UntypedFormGroup) => fg.patchValue({destinationUrl: this.bulkEditControls.destinationUrl.value}));
    } else if (bulkEditSelectValue === 'startDateTime') {
      rowControls.forEach((fg: UntypedFormGroup) => fg.patchValue({
        startDateStartOfDay: this.bulkEditControls.startDateStartOfDay.value,
        startDateHour: this.bulkEditControls.startDateHour.value
      }));
    } else if (bulkEditSelectValue === 'endDateTime') {
      rowControls.forEach((fg: UntypedFormGroup) => fg.patchValue({
        endDateStartOfDay: this.bulkEditControls.endDateStartOfDay.value,
        endDateHour: this.bulkEditControls.endDateHour.value
      }));
    } else if (bulkEditSelectValue === 'affiliateExclusive') {
      rowControls.forEach((fg: UntypedFormGroup) => fg.patchValue({affiliateExclusive: this.bulkEditControls.affiliateExclusive.value}));
      this.exclusiveFilterPreviousInputs = this.exclusiveFilterPreviousInputs.map(x => this.bulkEditControls.affiliateExclusive.value);
    }
  }

  onSaveClick() {
    this.savingInProgress = true;
    let rowValuesAndIndex = (<RowValue[]> this.topControls.rows.value).map((x, index) => ({...x, index}));

    let editMerchantAds$ = of(rowValuesAndIndex).pipe(
      switchMap(rowValues => {
          let editMerchantAds = rowValues.map(row => {
            let merchantAdRequest: EditMerchantAdRequest = {
              adTitle: row.adTitle,
              destinationUrl: row.destinationUrl,
              startDate: this.data.merchantAds[row.index].start_date > this.mtNow
                ? row.startDateStartOfDay && addHours(row.startDateStartOfDay, row.startDateHour) : null,
              endDate: row.endDateStartOfDay && addHours(row.endDateStartOfDay, row.endDateHour),
              restrictedEntityGroupName: '',
              restrictedEntityId: ''
            };

            if (this.data.merchantAds[row.index].ad_type === 'banner') {
              let fileInputElement = <HTMLInputElement> document.getElementById(`fileInput[${row.index}]`);
              merchantAdRequest.imageFile = fileInputElement?.files?.[0];
            } else {
              merchantAdRequest.linkText = row.linkText;
            }

            if (row.affiliateExclusive.filterType === 'affiliates' && row.affiliateExclusive.affiliate_name !== 'All Affiliates') {
              merchantAdRequest.restrictedEntityGroupName = 'affiliates';
              merchantAdRequest.restrictedEntityId = row.affiliateExclusive.affiliate_id;
            }

            if (row.affiliateExclusive.filterType === 'tag_groups') {
              merchantAdRequest.restrictedEntityGroupName = 'tag_groups';
              merchantAdRequest.restrictedEntityId = row.affiliateExclusive.tag_id;
            }

            {
              return this.merchantApiService.editMerchantAd(this.merchantId, this.data.merchantAds[row.index].merchant_ad_id, merchantAdRequest);
            }
          });

          return forkJoin(editMerchantAds).pipe(defaultIfEmpty(<MerchantAd[]> []));
        }
      )
    );

    editMerchantAds$.pipe(first()).subscribe({
      next: (result: MerchantAd[]) => {
        this.snackBarService.openSnackBar('Saved');
        this.dialogRef.close(true);
      },
      error: (errorResponse: HttpErrorResponse) => {
        this.snackBarService.openErrorSnackBar(errorResponse?.error?.error ?? 'An Error Occurred');
        this.saveErrorOccurred = true;
        this.savingInProgress = false;
        this.changeDetectorRef.detectChanges();

        if (this.data.merchantAds.length > 1) {
          this.dialogRef.close(true);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.ngAfterViewInit$.next(undefined);
    this.ngAfterViewInit$.complete();
  }

  ngOnDestroy() {
    this.ngOnDestroy$.next(undefined);
    this.ngOnDestroy$.complete();
  }
}
